import { env } from "@/shared/lib/env";
import { captureException, isSentryEnabled, setSentryUser } from "@/shared/lib/sentry";

/**
 * Web crash reporter. Mirrors the `CrashReporter` interface used by
 * `wanderfit-app` (`src/lib/crash-reporting.ts`) so both clients log and
 * report errors with the same shape.
 *
 * In `development` everything flushes to `console.*`. In `staging` /
 * `production` errors are sent to Sentry (see `sentry.ts`); when no DSN
 * is configured (`isSentryEnabled()` is false) the reporter falls back
 * to `console.error` so an error is never silently dropped. Informational
 * `log` stays quiet outside development to keep prod consoles clean.
 */
export interface CrashReporter {
  readonly log: (message: string) => void;
  readonly recordError: (error: Error, context?: Readonly<Record<string, string>>) => void;
  readonly setUserId: (userId: string | null) => void;
}

const isDev = env.VITE_APP_ENV === "development";

function consoleRecordError(error: Error, context?: Readonly<Record<string, string>>): void {
  console.error("[CrashReporting]", error, context ?? {});
}

function createDevCrashReporter(): CrashReporter {
  return {
    log(message) {
      console.log(`[CrashReporting] ${message}`);
    },
    recordError: consoleRecordError,
    setUserId(userId) {
      console.log(`[CrashReporting] setUserId=${userId ?? "<null>"}`);
    },
  };
}

function createProdCrashReporter(): CrashReporter {
  return {
    log() {},
    recordError(error, context) {
      if (isSentryEnabled()) {
        captureException(error, context);
        return;
      }
      // Defensive fallback: a deploy without a DSN must still surface
      // errors somewhere rather than swallow them.
      consoleRecordError(error, context);
    },
    setUserId(userId) {
      if (isSentryEnabled()) {
        setSentryUser(userId);
      }
    },
  };
}

export const crashReporting: CrashReporter = isDev
  ? createDevCrashReporter()
  : createProdCrashReporter();
